import {
  DeliveryDropOffLocationDto,
  DropOffLocationDtoV2,
  LatLng,
  LocationDto,
  LocationDtoV2
} from '../models/locations';
import { DeliveryAction, PaymentOnDeliveryType } from '../models/deliveries';
import { TimeSlotWithDate } from '../models/timeslot';

export interface DriverDetailsDto {
  driver_name: string;
  driver_number: string;
  notes_to_driver: string;
  driver_picture: string;
}

export interface CurrencyModelDto {
  display_code: string;
}

export interface PassengerDto {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface VehicleDto {
  model: string;
  make: string;
  license_plate: string;
  color: string;
}

export interface DeliveryDto {
  booking_id: string;
  solutions_reference: number;
  status: string;
  booking_time: number;
  pickup_details?: LocationDto;
  dropoff_details?: DeliveryDropOffLocationDto;
  driver_details?: DriverDetailsDto;
  trip_distance?: number;
  trip_price?: number;
  dropoff_time?: number;
  currency_model?: CurrencyModelDto;
  is_cnf?: boolean;
  eta?: number;
  driver_notes?: string;
  passenger?: PassengerDto;
  vehicle_details?: VehicleDto;
  delivery_action?: DeliveryAction;
  booking_type?: DeliveryOptionDto;
  expected_delivery_window?: TimeSlotWithDate;
  expected_pickup_time?: string;
}

export type DeliveryDetailsDto = {
  id: string;
  reference: number;
  pickup: LocationDtoV2;
  payment: {
    payment_id: number;
    use_credit?: boolean;
    reference_code?: string;
  };
  created_at: number;
  cash_in?: {
    amount: number;
  };
  cash_out?: {
    amount: number;
  };
  order?: {
    reference?: string;
    type?: 'LMD';
    items?: DeliveryItemDto[];
  };
  dropoff?: DropOffLocationDtoV2;
  notes?: string;
  captain_details?: CaptainDetailsDto;
  trip_details?: {
    delivery_distance_in_Km: number;
    pricing?: PricingModelDto;
  };
  proof_of_delivery_image_url?: string;
  cancellation_reason?: CancellationReason;
  drop_off_location_edited?: boolean;
};

export type PricingModelDto = {
  trip_cost: number;
  currency: string;
};

export type CaptainDetailsDto = {
  name: string;
  profilePicture: string;
  cellPhone: string;
  licensePlate: string;
  model: string;
  color: string;
};

export interface PastDeliveryDto {
  status: string;
  id: string;
  reference: number;
  payment: {
    payment_id: number;
    use_credit?: boolean;
    reference_code?: string;
  };
  created_at: number;
  cash_in?: {
    amount: number;
  };
  cash_out?: {
    amount: number;
  };
  order?: {
    reference?: string;
    type?: 'LMD';
    items?: DeliveryItemDto[];
  };
  notes?: string;
  customer_name?: string;
  pickup_name?: string;
  completed_at?: number;
  customer_phoneNumber?: string;
  cancellation_reason?: CancellationReason;
  delivery_type?: DeliveryOptionDto;
}

export interface CustomerDetails {
  name: string;
  phone_number: string;
  email: string;
}

export interface DeliveryItemDto {
  name: string;
  count: number;
  instructions: string;
  options: string[];
}

export enum OrderType {
  LMD = 'LMD',
  LMD_DELIVERY = 'LMD_DELIVERY',
  LMD_SPECIAL = 'LMD_SPECIAL',
  LMD_TIERED = 'LMD_TIERED',
  LMD_CAR = 'LMD_CAR'
}

export interface DeliveryRequestDto {
  cash_in?: {
    amount: number;
    payment_type?: PaymentOnDeliveryType;
  };
  cash_out?: {
    amount: number;
    payment_type?: PaymentOnDeliveryType;
  };
  order?: {
    reference?: string;
    type?: OrderType;
    items?: DeliveryItemDto[];
  };
  pickup: LocationDtoV2;
  dropoff?: DropOffLocationDtoV2;
  payment: {
    payment_id: number;
    use_credit?: boolean;
    reference_code?: string;
    payment_type?: string;
  };
  notes?: string;
  delivery_action?: DeliveryAction;
  delivery_window?: TimeSlotWithDate;
}

export enum DeliveryReportStatus {
  PROCESSING = 'PROCESSING',
  READY = 'READY',
  FAILED = 'FAILED'
}

export interface DeliveryReportDto {
  id: string;
  status: DeliveryReportStatus;
  url?: string;
  start_date: string;
  end_date: string;
}

export interface DeliveryReport {
  id?: string;
  status?: DeliveryReportStatus;
  url?: string;
}

export enum CancellationReason {
  DUPLICATE_BOOKING_CREATED = 'DUPLICATE_BOOKING_CREATED',
  TIME_SHOWN_FOR_CAPTAIN_ARRIVAL_TOO_HIGH = 'TIME_SHOWN_FOR_CAPTAIN_ARRIVAL_TOO_HIGH',
  CAPTAIN_NOT_REACHABLE = 'CAPTAIN_NOT_REACHABLE',
  CAPTAIN_LATE_FOR_PICKUP = 'CAPTAIN_LATE_FOR_PICKUP',
  ORDER_ALREADY_PICKED_UP = 'ORDER_ALREADY_PICKED_UP',
  CAPTAIN_REFUSED_TO_PICK_UP = 'CAPTAIN_REFUSED_TO_PICK_UP',
  ORDER_SIZE_TOO_BIG_FOR_CAPTAIN_TO_DELIVER = 'ORDER_SIZE_TOO_BIG_FOR_CAPTAIN_TO_DELIVER',
  CAPTAIN_CANT_FIND_CUSTOMER_LOCATION = 'CAPTAIN_CANT_FIND_CUSTOMER_LOCATION',
  OTHER = 'OTHER'
}

export type DeliveryPriceEstimationResponseDto = {
  estimated_trip_cost: number;
  estimated_distance: number;
  currency: string;
};

export type DeliveryPriceEstimationRequestDto = {
  type: OrderType;
  pickup: CoordinateDto;
  dropoff: CoordinateDto;
};

export type CoordinateDto = {
  coordinate: LatLng;
};

export interface ActivePassengerV2Dto {
  name: string;
  phone_number: string;
}

export interface ActiveUserDeliveryV2Dto {
  delivery_id: string;
  created_at: number;
  solutions_reference: string;
  delivery_type: string;
  status: string;
  pickup_details?: LocationDto;
  dropoff_details?: DeliveryDropOffLocationDto;
  driver_details?: DriverDetailsDto | null;
  vehicle_details?: VehicleDto | null;
  driver_notes?: string;
  delivery_action?: DeliveryAction;
  expected_delivery_window?: TimeSlotWithDate;
  expected_pickup_time?: string;
  passenger?: ActivePassengerV2Dto;
  drop_off_location_edited?: boolean;
}

export interface ActiveDeliveriesPageResponseDto {
  deliveries: ActiveUserDeliveryV2Dto[];
  next_start_key_id: string | null;
  next_start_key_time: number | null;
}

export enum DeliveryTypeParam {
  INSTANT = 'active',
  SCHEDULED = 'scheduled'
}

export enum DeliveryOptionDto {
  INSTANT = 'bullet',
  SCHEDULED = 'scheduled'
}
